import { AI_VISIBILITY_PLATFORMS } from '@brightlocal/shared';
import type { AiVisibilityPlatform } from '@brightlocal/shared';
import type { BadgeProps } from '@brightlocal/ui-components/badge';

import type { BreakdownMetricKey, MetricColumnConfig, MetricFormat } from './types';

/** UI-facing classification of an AI Visibility query failure — see `AiVisibilityErrorType`. */
export const AI_VISIBILITY_ERROR_TYPES = {
  NOT_RUN_YET: 'notRunYet',
  ACCESS_DENIED: 'accessDenied',
  RATE_LIMITED: 'rateLimited',
  UNKNOWN: 'unknown'
} as const;

/**
 * Client-side page size of the prompts table. A report carries at most 20 prompts and they all
 * arrive in one response, so the table paginates locally (~2 pages).
 */
export const PROMPTS_PAGE_SIZE = 10;

/**
 * Client-side page size of the competitors table. The full competitor set arrives in one
 * response, so the table paginates locally, mirroring the prompts table.
 */
export const COMPETITORS_PAGE_SIZE = 10;

/**
 * Client-side page size of the AI Sources table. The full sources set arrives in one response,
 * so the table paginates locally, mirroring the prompts and competitors tables.
 */
export const SOURCES_PAGE_SIZE = 10;

/**
 * Presentation order of the AI platforms everywhere they are listed on the report (chart bars,
 * the platform filter options, the citing platforms of a source): ChatGPT first, Google AI
 * Overview last — ChatGPT usually
 * carries the richest data set, Google AI Overview the sparsest. The dictionary keeps the
 * declaration order.
 */
export const PLATFORM_DISPLAY_ORDER = [
  AI_VISIBILITY_PLATFORMS.CHATGPT,
  AI_VISIBILITY_PLATFORMS.GOOGLE_AI_MODE,
  AI_VISIBILITY_PLATFORMS.GOOGLE_AI_OVERVIEW
] as const satisfies readonly AiVisibilityPlatform[];

/** Sentinel value of the platform filter meaning "no platform filter" — cards show `overall`. */
export const ALL_PLATFORMS = 'all';

/** Value of the prompt-type filter showing the full metric set (branded + unbranded prompts). */
export const ALL_PROMPT_TYPES = 'all';

/** Value of the prompt-type filter showing the non-branded metric twins. */
export const UNBRANDED_PROMPT_TYPE = 'unbranded';

/** Placeholder rendered on a metric card when the selected slice has no data behind it. */
export const NO_METRIC_DATA_PLACEHOLDER = '-';

/** Display formats of breakdown metric values — score, percent, or `#`-prefixed position. */
export const METRIC_FORMATS = {
  SCORE: 'score',
  PERCENT: 'percent',
  POSITION: 'position'
} as const;

/**
 * How often the runs-list query re-polls while a run is executing. Runs take minutes, so a
 * 15s interval keeps the page responsive without hammering the API.
 */
export const AI_VISIBILITY_RUN_POLL_INTERVAL_MS = 15_000;

/**
 * Page size of the runs-list query (API default is 25, max 100) — one page this size covers
 * the page's needs. Accepted cut-off: a completed run older than the newest 50 runs is treated
 * as absent, so 50 consecutive failed runs would surface the first-run-failed state.
 */
export const AI_VISIBILITY_RUNS_FETCH_LIMIT = 50;

/**
 * Height (px) of the chart slot on the overview cards — shared by the bar chart, its empty
 * state, and the loading skeleton so the cards never jump between states.
 */
export const OVERVIEW_CHART_HEIGHT = 240;

/**
 * Bar colour of each AI platform on the overview charts — DS chart tokens, kept consistent
 * across all four cards. Interim palette until design confirms the platform colours.
 */
export const PLATFORM_CHART_COLORS: Record<AiVisibilityPlatform, string> = {
  google_ai_overview: 'var(--chart-1)',
  google_ai_mode: 'var(--chart-2)',
  chatgpt: 'var(--chart-3)'
};

/** i18n label keys of each AI platform, shown as the platform-filter options. */
export const PLATFORM_LABEL_KEYS: Record<AiVisibilityPlatform, string> = {
  google_ai_overview: 'reports.aiVisibility.breakdown.platforms.googleAiOverview',
  google_ai_mode: 'reports.aiVisibility.breakdown.platforms.googleAiMode',
  chatgpt: 'reports.aiVisibility.breakdown.platforms.chatgpt'
};

/** Static config of a single metric card in the breakdown section. */
interface MetricCardConfig {
  /** Field of the normalised metric set the card renders. */
  key: BreakdownMetricKey;
  /** Display format of the value. */
  format: MetricFormat;
  /** Full i18n key of the metric label. */
  labelKey: string;
  /** Full i18n key of the metric tooltip body. */
  tooltipKey: string;
  /** Full i18n key of a muted suffix after the headline value (e.g. "/100"). */
  valueSuffixKey?: string;
}

/** Ordered config of the four breakdown metric cards (AC: score, mention rate, share of voice, avg position). */
export const METRIC_CARDS: readonly MetricCardConfig[] = [
  {
    key: 'visibilityScore',
    format: METRIC_FORMATS.SCORE,
    labelKey: 'reports.aiVisibility.breakdown.metrics.visibilityScore.label',
    tooltipKey: 'reports.aiVisibility.tooltips.visibilityScore',
    valueSuffixKey: 'reports.aiVisibility.breakdown.metrics.visibilityScore.suffix'
  },
  {
    key: 'mentionRate',
    format: METRIC_FORMATS.PERCENT,
    labelKey: 'reports.aiVisibility.breakdown.metrics.mentionRate.label',
    tooltipKey: 'reports.aiVisibility.tooltips.mentionRate'
  },
  {
    key: 'shareOfVoice',
    format: METRIC_FORMATS.PERCENT,
    labelKey: 'reports.aiVisibility.breakdown.metrics.shareOfVoice.label',
    tooltipKey: 'reports.aiVisibility.tooltips.shareOfVoice'
  },
  {
    key: 'avgPosition',
    format: METRIC_FORMATS.POSITION,
    labelKey: 'reports.aiVisibility.breakdown.metrics.avgPosition.label',
    tooltipKey: 'reports.aiVisibility.tooltips.avgPosition'
  }
];

/** Column ids of the prompts table. */
export const PROMPTS_TABLE_COLUMNS = {
  PROMPT: 'prompt',
  TYPE: 'type',
  VISIBILITY_SCORE: 'visibilityScore',
  MENTION_RATE: 'mentionRate',
  SHARE_OF_VOICE: 'shareOfVoice',
  AVG_POSITION: 'avgPosition'
} as const;

/** Ordered config of the four metric columns of the prompts table, following the design. */
export const PROMPT_METRIC_COLUMNS: readonly MetricColumnConfig[] = [
  {
    key: PROMPTS_TABLE_COLUMNS.VISIBILITY_SCORE,
    format: METRIC_FORMATS.SCORE,
    labelKey: 'reports.aiVisibility.prompts.columns.visibilityScore',
    tooltipKey: 'reports.aiVisibility.tooltips.visibilityScore',
    dataHook: 'visibility-score'
  },
  {
    key: PROMPTS_TABLE_COLUMNS.MENTION_RATE,
    format: METRIC_FORMATS.PERCENT,
    labelKey: 'reports.aiVisibility.prompts.columns.mentionRate',
    tooltipKey: 'reports.aiVisibility.tooltips.mentionRate',
    dataHook: 'mention-rate'
  },
  {
    key: PROMPTS_TABLE_COLUMNS.SHARE_OF_VOICE,
    format: METRIC_FORMATS.PERCENT,
    labelKey: 'reports.aiVisibility.prompts.columns.shareOfVoice',
    tooltipKey: 'reports.aiVisibility.tooltips.shareOfVoice',
    dataHook: 'share-of-voice'
  },
  {
    key: PROMPTS_TABLE_COLUMNS.AVG_POSITION,
    format: METRIC_FORMATS.POSITION,
    labelKey: 'reports.aiVisibility.prompts.columns.avgPosition',
    tooltipKey: 'reports.aiVisibility.tooltips.avgPosition',
    dataHook: 'avg-position'
  }
];

/** GTM `eventLabel` values of the row expand/collapse toggle events (GA-spec controlled enum). */
export const ROW_TOGGLE_EVENT_LABELS = {
  EXPAND: 'expand',
  COLLAPSE: 'collapse'
} as const;

/** Column ids of the competitors table (the trailing expander column is owned by ExpandableMetricsTable). */
export const COMPETITORS_TABLE_COLUMNS = {
  BUSINESS: 'business',
  VISIBILITY_SCORE: 'visibilityScore',
  MENTION_RATE: 'mentionRate',
  SHARE_OF_VOICE: 'shareOfVoice',
  AVG_POSITION: 'avgPosition'
} as const;

/**
 * Ordered config of the four metric columns of the competitors table. Share of voice renders an
 * empty cell on the platform sub-rows: the API carries no per-platform value for competitors, so
 * the cell stays blank until the upstream data exists
 * [https://brightlocal.atlassian.net/browse/FP-212].
 */
export const COMPETITOR_METRIC_COLUMNS: readonly MetricColumnConfig[] = [
  {
    key: COMPETITORS_TABLE_COLUMNS.VISIBILITY_SCORE,
    format: METRIC_FORMATS.SCORE,
    labelKey: 'reports.aiVisibility.competitors.columns.visibilityScore',
    tooltipKey: 'reports.aiVisibility.tooltips.visibilityScore',
    dataHook: 'visibility-score'
  },
  {
    key: COMPETITORS_TABLE_COLUMNS.MENTION_RATE,
    format: METRIC_FORMATS.PERCENT,
    labelKey: 'reports.aiVisibility.competitors.columns.mentionRate',
    tooltipKey: 'reports.aiVisibility.tooltips.mentionRate',
    dataHook: 'mention-rate'
  },
  {
    key: COMPETITORS_TABLE_COLUMNS.SHARE_OF_VOICE,
    format: METRIC_FORMATS.PERCENT,
    labelKey: 'reports.aiVisibility.competitors.columns.shareOfVoice',
    tooltipKey: 'reports.aiVisibility.tooltips.shareOfVoice',
    dataHook: 'share-of-voice',
    isHiddenOnSubRows: true
  },
  {
    key: COMPETITORS_TABLE_COLUMNS.AVG_POSITION,
    format: METRIC_FORMATS.POSITION,
    labelKey: 'reports.aiVisibility.competitors.columns.avgPosition',
    tooltipKey: 'reports.aiVisibility.tooltips.avgPosition',
    dataHook: 'avg-position'
  }
];

/** Column ids of the AI Sources table. */
export const SOURCES_TABLE_COLUMNS = {
  DOMAIN: 'domain',
  CITATIONS: 'citations',
  COVERAGE: 'coverage',
  CITED_BY: 'citedBy'
} as const;

/** Row field each sortable AI Sources column orders by — a column missing here cannot be sorted. */
export const SOURCES_SORT_KEYS = {
  [SOURCES_TABLE_COLUMNS.CITATIONS]: 'citationCount',
  [SOURCES_TABLE_COLUMNS.COVERAGE]: 'coverage'
} as const;

/** GTM `eventAction` values of the `filter_chosen` event — which overview filter was changed. */
export const AI_VISIBILITY_FILTER_ACTION = {
  AI_PLATFORM: 'ai_platform',
  PROMPT_TYPE: 'prompt_type'
} as const;

/** GTM `eventAction` values of the `update_data_clicked` event — where the run was started from. */
export const UPDATE_DATA_SOURCE = {
  HEADER: 'header',
  FAILED_RUN_ALERT: 'failed_run_alert',
  FAILED_RUN_SCREEN: 'failed_run_screen'
} as const;

/** GTM `eventAction` values of the `metric_info_viewed` event — the section the tooltip was opened in. */
export const METRIC_INFO_SECTION = {
  PLATFORMS: 'platforms',
  COMPETITORS: 'competitors',
  PROMPTS: 'prompts'
} as const;

/** GTM `eventLabel` of the platform filter's "all" sentinel (the GA spec value, not the URL sentinel). */
export const ALL_PLATFORMS_EVENT_LABEL = 'all_platforms';

/** GTM `eventLabel` of the prompt-type filter's "all" sentinel (the GA spec value, not the URL sentinel). */
export const ALL_PROMPT_TYPES_EVENT_LABEL = 'all_prompts';

/** GTM `eventLabel` of each metric on the `metric_info_viewed` event. */
export const METRIC_EVENT_LABELS = {
  visibilityScore: 'visibility_score',
  mentionRate: 'mention_rate',
  shareOfVoice: 'share_of_voice',
  avgPosition: 'avg_position'
} as const satisfies Record<BreakdownMetricKey, string>;

/** Insight filter values for the insights panel — both competitor types share one group. */
export const INSIGHT_FILTERS = {
  ALL: 'all',
  STRENGTH: 'strength',
  WEAKNESS: 'weakness',
  OPPORTUNITY: 'opportunity',
  COMPETITORS: 'competitors'
} as const;

/** Filter options in display order, paired with their option-label keys. */
export const INSIGHT_FILTER_OPTIONS = [
  { value: INSIGHT_FILTERS.ALL, labelKey: 'reports.aiVisibility.insightsPanel.filters.all' },
  { value: INSIGHT_FILTERS.STRENGTH, labelKey: 'reports.aiVisibility.insightsPanel.filters.strength' },
  { value: INSIGHT_FILTERS.WEAKNESS, labelKey: 'reports.aiVisibility.insightsPanel.filters.weakness' },
  { value: INSIGHT_FILTERS.OPPORTUNITY, labelKey: 'reports.aiVisibility.insightsPanel.filters.opportunity' },
  { value: INSIGHT_FILTERS.COMPETITORS, labelKey: 'reports.aiVisibility.insightsPanel.filters.competitors' }
] as const;

/**
 * Insight types the panel renders. Spectra's prompt emits exactly these; the
 * data layer drops anything else before it reaches the panel (`isInsightType`).
 */
export const INSIGHT_TYPES = [
  'strength',
  'weakness',
  'competitor_strength',
  'competitor_weakness',
  'opportunity'
] as const;

/** One of the insight types the panel renders ({@link INSIGHT_TYPES}). */
export type InsightType = (typeof INSIGHT_TYPES)[number];

/**
 * Badge colour per insight type. DS `Badge` ships only two semantic tints —
 * `primary` (green) and `destructive` (red) — so opportunity and competitor
 * borrow the neutral ones; a warning/info variant would give all four a colour.
 */
export const INSIGHT_BADGE_VARIANTS: Record<InsightType, NonNullable<BadgeProps['variant']>> = {
  strength: 'primary',
  weakness: 'destructive',
  competitor_strength: 'secondary',
  competitor_weakness: 'secondary',
  opportunity: 'outline'
};

/** Card title key per insight type. */
export const INSIGHT_CARD_TITLE_KEYS: Record<InsightType, string> = {
  strength: 'reports.aiVisibility.insightsPanel.cardTitles.strength',
  weakness: 'reports.aiVisibility.insightsPanel.cardTitles.weakness',
  competitor_strength: 'reports.aiVisibility.insightsPanel.cardTitles.competitor',
  competitor_weakness: 'reports.aiVisibility.insightsPanel.cardTitles.competitor',
  opportunity: 'reports.aiVisibility.insightsPanel.cardTitles.opportunity'
};
